import { React, useEffect, useState } from "react";

import {
    AspectRatio, Box, Button, Card, CardBody, Center, Checkbox, CheckboxGroup, Select,
    Flex, Grid, GridItem, Heading, Image, Link, Radio, RadioGroup, Spacer, Text, Tooltip, useToast
} from "@chakra-ui/react";

import salmonRunImage from "../images/splatoon/salmon_run.webp";
import eventsImage from "../images/splatoon/events.webp";
import regularBattleImage from "../images/splatoon/regular.webp";
import bankaraBattleImage from "../images/splatoon/bankara.webp";
import xBattleImage from "../images/splatoon/x.webp";

import splashtag from "../images/splatoon/splashtag.webp";

import { AppMenuButtons } from "../parts/AppMenuButtons";
import { Helmet } from "react-helmet";

const CALENDAR_SERVER = "splatoon.shiky.me";


const CalendarCard = (props) => {

    const toast = useToast();

    return <GridItem><Card w={["100%", "100%", "420px"]} height="100%">
        <CardBody boxShadow="lg">
            <AspectRatio ratio={1 / 1}>
                <Image src={props.bannerImage} borderRadius="lg" />
            </AspectRatio>
            <Flex mt={6} direction="column" gap={3} minHeight="max-content">
                <Heading size="md" fontFamily="Splatfont1">{props.title}</Heading>
                {props.children}
                <Spacer />
                <Flex gap={3} direction={["column", "row", "row", "row"]}>
                    <Tooltip label="Suitable for iCloud and Outlook calendars">
                        <Button fontFamily="Splatfont2" variant="solid" colorScheme="blue" onClick={() => {
                            window.open("webcal://" + props.baseUrl);
                        }}>
                            WebCal
                        </Button>
                    </Tooltip>
                    <Button fontFamily="Splatfont2" variant="solid" colorScheme="blue" onClick={() => {
                        const encodedUrl = encodeURIComponent("webcal://" + props.baseUrl);
                        window.open("https://www.google.com/calendar/r?cid=" + encodedUrl);
                    }} >
                        Google Calendar
                    </Button>
                    <Button fontFamily="Splatfont2" onClick={() => {
                        navigator.clipboard.writeText("https://" + props.baseUrl);
                        toast({
                            title: "Copied!",
                            description: "The calendar link has been copied to the clipboard.",
                            status: "success",
                            duration: 3000,
                            isClosable: true
                        });
                    }}>
                        Copy Link
                    </Button>
                </Flex>
            </Flex>
        </CardBody>
    </Card></GridItem>;
}

const CalendarFeed = () => {

    const [fetchedStages, setFetchedStages] = useState({});
    const [fetchedRules, setFetchedRules] = useState({});

    const [regularStages, setRegularStages] = useState(new Set());
    const [regularScope, setRegularScope] = useState("future");

    const [bankaraStages, setBankaraStages] = useState(new Set());
    const [bankaraRules, setBankaraRules] = useState(new Set());
    const [bankaraMode, setBankaraMode] = useState("both");
    const [bankaraScope, setBankaraScope] = useState("future");

    const [xStages, setXStages] = useState(new Set());
    const [xRules, setXRules] = useState(new Set());
    const [xScope, setXScope] = useState("future");

    const [salmonScope, setSalmonlScope] = useState("future");
    const [specialScope, setSpecialScope] = useState("future");

    const localStorageLocaleKey = "splatoonCalendarLocale";
    const [selectedLocale, setSelectedLocale] = useState("");

    useEffect(() => {

        document.title = "Splatoon 3 Calendars";

        const asyncFetch = async () => {
            try {
                const response = await fetch(`https://${CALENDAR_SERVER}/keys?lang=${localStorage.getItem("splatoonCalendarLocale")}`);
                const json = await response.json();

                var sr = {};
                var vs = {};
                var rs = {};

                for (var s in json["stages"]) {
                    if (json["stages"][s] === "") {
                        continue;
                    }
                    if (s.includes("Q29vcFN0YWdlL")) {
                        sr[s] = json["stages"][s];
                    }
                    if (s.includes("VnNTdGFnZS")) {
                        vs[s] = json["stages"][s];
                    }
                }
                for (var r in json["rules"]) {
                    if (r !== "undefined" && r !== "VnNSdWxlLTA=") {
                        rs[r] = json["rules"][r];
                    }
                }

                setFetchedStages(vs);
                setFetchedRules(rs);
            } catch (error) {
                console.log('Data fetch failed', error);
            }
        };

        asyncFetch();

        const validLocale = new Set([
            "de-DE", "en-US", "en-GB", "es-ES",
            "es-MX", "fr-FR", "fr-CA", "it-IT",
            "ja-JP", "ko-KR", "nl-NL", "ru-RU",
            "zh-CN", "zh-TW"
        ]);

        const storedLocale = localStorage.getItem(localStorageLocaleKey);
        if (validLocale.has(storedLocale)) {
            setSelectedLocale(storedLocale);
        } else {
            localStorage.setItem(localStorageLocaleKey, "en-US");
            setSelectedLocale("en-US");
        }

    }, [selectedLocale]);

    const CalendarLocaleSelect = <Select variant="filled" fontFamily="Splatfont2" w="-moz-fit-content" value={selectedLocale} onChange={(e) => {
        const newLocale = e.target.value;
        setSelectedLocale(newLocale);
        localStorage.setItem(localStorageLocaleKey, newLocale);
    }}>
        <option value="de-DE">Deutsch</option>
        <option value="en-US">English (US)</option>
        <option value="en-GB">English (GB)</option>
        <option value="es-ES">Español (ES)</option>
        <option value="es-MX">Español (MX)</option>
        <option value="fr-FR">Français (FR)</option>
        <option value="fr-CA">Français (CA)</option>
        <option value="it-IT">Italiano</option>
        <option value="ja-JP">日本語</option>
        <option value="ko-KR">한국어</option>
        <option value="nl-NL">Nederlands</option>
        <option value="ru-RU">Русский</option>
        <option value="zh-CN">简体中文</option>
        <option value="zh-TW">繁體中文</option>
    </Select>

    return <Center>
        <Helmet>
            <meta name="description" content={"A calendar feed for Splatoon 3 players."} />
        </Helmet>
        <Box mt="72px" ml="18px" mr="18px" mb="18px" p="12px">
            <Grid gap="12px" templateColumns={["1fr", "1fr", "1fr", "1fr 1fr", "1fr 1fr", "1fr 1fr 1fr"]} justifyContent="center">

                <CalendarCard
                    title="Regular Battle"
                    bannerImage={regularBattleImage}
                    baseUrl={`${CALENDAR_SERVER}/regular?lang=${localStorage.getItem("splatoonCalendarLocale")}&stages=${Array.from(regularStages).join(";")}&scope=${regularScope}`}
                >
                    <Text fontFamily="Splatfont2">
                        Check the stages you want to include in the calendar. If you don't choose any, all stages will be included.
                    </Text>
                    <Flex direction="column">
                        <Text fontFamily="Splatfont2" alignItems="left">Stages to include (check none to include all):</Text>
                        <CheckboxGroup>
                            <Grid templateColumns={"1fr 1.1fr"}>{
                                Object.keys(fetchedStages).map(key =>
                                    <GridItem key={"g_re_" + key}><Checkbox key={"c_re_" + key} fontFamily={selectedLocale === "zh-CN" || selectedLocale === "zh-TW" ? "": "Splatfont2"} onChange={(e) => {
                                        if (e.target.checked) {
                                            setRegularStages(prev => new Set([...prev, key]))
                                        } else {
                                            setRegularStages(prev => new Set([...prev].filter(x => x !== key)))
                                        }
                                    }}>{fetchedStages[key]}</Checkbox></GridItem>)
                            }</Grid>
                        </CheckboxGroup>
                        <Text mt={2} fontFamily="Splatfont2" alignItems="left" >Calendar scope:</Text>
                        <RadioGroup onChange={setRegularScope} value={regularScope}>
                            <Flex direction="row" gap={6}>
                                <Radio value="future"><Text fontFamily="Splatfont2">Future only</Text></Radio>
                                <Radio value="week"><Text fontFamily="Splatfont2">Future and past week</Text></Radio>
                            </Flex>
                        </RadioGroup>
                    </Flex>
                </CalendarCard>

                <CalendarCard
                    title="Bankara Battle"
                    bannerImage={bankaraBattleImage}
                    baseUrl={`${CALENDAR_SERVER}/bankara?lang=${localStorage.getItem("splatoonCalendarLocale")}&stages=${Array.from(bankaraStages).join(";")}&rules=${Array.from(bankaraRules).join(";")}&mode=${bankaraMode}&scope=${bankaraScope}`}
                >
                    <Text fontFamily="Splatfont2">
                        Check the stages, game rules, and game modes you want to include in the calendar. If you don't choose any, all stages/rules will be included.
                    </Text>
                    <Flex direction="column">
                        <Text fontFamily="Splatfont2" alignItems="left">Stages to include (check none to include all):</Text>
                        <CheckboxGroup>
                            <Grid templateColumns={"1fr 1.1fr"}>{
                                Object.keys(fetchedStages).map(key =>
                                    <GridItem key={"g_ba_s_" + key}><Checkbox key={"c_ba_s_" + key} fontFamily={selectedLocale === "zh-CN" || selectedLocale === "zh-TW" ? "": "Splatfont2"} onChange={(e) => {
                                        if (e.target.checked) {
                                            setBankaraStages(prev => new Set([...prev, key]))
                                        } else {
                                            setBankaraStages(prev => new Set([...prev].filter(x => x !== key)))
                                        }
                                    }}>{fetchedStages[key]}</Checkbox></GridItem>)
                            }</Grid>
                        </CheckboxGroup>
                        <Text mt={2} fontFamily="Splatfont2" alignItems="left">And rules to include (check none to include all):</Text>
                        <CheckboxGroup>
                            <Grid templateColumns={"1fr 1.1fr"}>{
                                Object.keys(fetchedRules).map(key =>
                                    <GridItem key={"g_ba_r_" + key}><Checkbox key={"c_ba_r_" + key} fontFamily={selectedLocale === "zh-CN" || selectedLocale === "zh-TW" ? "": "Splatfont2"} onChange={(e) => {
                                        if (e.target.checked) {
                                            setBankaraRules(prev => new Set([...prev, key]))
                                        } else {
                                            setBankaraRules(prev => new Set([...prev].filter(x => x !== key)))
                                        }
                                    }}>{fetchedRules[key]}</Checkbox></GridItem>)
                            }</Grid>
                        </CheckboxGroup>
                        <Text mt={2} fontFamily="Splatfont2" alignItems="left">And game modes to include:</Text>
                        <RadioGroup onChange={setBankaraMode} value={bankaraMode}>
                            <Flex direction="horizontal" gap={5}>
                                <Radio value="challenge"><Text fontFamily="Splatfont2">Challenge</Text></Radio>
                                <Radio value="open"><Text fontFamily="Splatfont2">Open</Text></Radio>
                                <Radio value="both"><Text fontFamily="Splatfont2">Both</Text></Radio>
                            </Flex>
                        </RadioGroup>
                        <Text mt={2} fontFamily="Splatfont2" alignItems="left">Calendar scope:</Text>
                        <RadioGroup onChange={setBankaraScope} value={bankaraScope}>
                            <Flex direction="row" gap={6}>
                                <Radio value="future"><Text fontFamily="Splatfont2">Future only</Text></Radio>
                                <Radio value="week"><Text fontFamily="Splatfont2">Future and past week</Text></Radio>
                            </Flex>
                        </RadioGroup>
                    </Flex>
                </CalendarCard>

                <CalendarCard
                    title="X Battle"
                    bannerImage={xBattleImage}
                    baseUrl={`${CALENDAR_SERVER}/x?lang=${localStorage.getItem("splatoonCalendarLocale")}&stages=${Array.from(xStages).join(";")}&rules=${Array.from(xRules).join(";")}&scope=${xScope}`}
                >
                    <Text fontFamily="Splatfont2">
                        Check the stages and game rules you want to include in the calendar. If you don't choose any, all stages/rules will be included.
                    </Text>
                    <Flex direction="column">
                        <Text fontFamily="Splatfont2" alignItems="left">Stages to include (check none to include all):</Text>
                        <CheckboxGroup>
                            <Grid templateColumns={"1fr 1.1fr"}>{
                                Object.keys(fetchedStages).map(key =>
                                    <GridItem key={"g_x_s_" + key}><Checkbox key={"c_x_s_" + key} fontFamily={selectedLocale === "zh-CN" || selectedLocale === "zh-TW" ? "": "Splatfont2"} onChange={(e) => {
                                        if (e.target.checked) {
                                            setXStages(prev => new Set([...prev, key]))
                                        } else {
                                            setXStages(prev => new Set([...prev].filter(x => x !== key)))
                                        }
                                    }}>{fetchedStages[key]}</Checkbox></GridItem>)
                            }</Grid>
                        </CheckboxGroup>
                        <Text mt={2} fontFamily="Splatfont2" alignItems="left">And rules to include (check none to include all):</Text>
                        <CheckboxGroup>
                            <Grid templateColumns={"1fr 1.1fr"}>{
                                Object.keys(fetchedRules).map(key =>
                                    <GridItem key={"g_x_r_" + key}><Checkbox key={"c_x_r_" + key} fontFamily={selectedLocale === "zh-CN" || selectedLocale === "zh-TW" ? "": "Splatfont2"} onChange={(e) => {
                                        if (e.target.checked) {
                                            setXRules(prev => new Set([...prev, key]))
                                        } else {
                                            setXRules(prev => new Set([...prev].filter(x => x !== key)))
                                        }
                                    }}>{fetchedRules[key]}</Checkbox></GridItem>)
                            }</Grid>
                        </CheckboxGroup>
                        <Text mt={2} fontFamily="Splatfont2" alignItems="left">Calendar scope:</Text>
                        <RadioGroup onChange={setXScope} value={xScope}>
                            <Flex direction="row" gap={6}>
                                <Radio value="future"><Text fontFamily="Splatfont2">Future only</Text></Radio>
                                <Radio value="week"><Text fontFamily="Splatfont2">Future and past week</Text></Radio>
                            </Flex>
                        </RadioGroup>
                    </Flex>
                </CalendarCard>

                <CalendarCard
                    title="Salmon Run"
                    bannerImage={salmonRunImage}
                    baseUrl={`${CALENDAR_SERVER}/salmonrun?lang=${localStorage.getItem("splatoonCalendarLocale")}&scope=${salmonScope}`}
                >
                    <Flex direction="column">
                        <Text fontFamily="Splatfont2">
                            This calendar includes all the Salmon Run schedules, including random-weapon shifts, but does not include Big Runs or Eggstra Work. To see Big Run and Eggstra Work schedules, use the special events calendar.
                        </Text>
                        <Text mt={2} fontFamily="Splatfont2" alignItems="left">Calendar scope:</Text>
                        <RadioGroup onChange={setSalmonlScope} value={salmonScope}>
                            <Flex direction="row" gap={6}>
                                <Radio value="future"><Text fontFamily="Splatfont2">Future only</Text></Radio>
                                <Radio value="month"><Text fontFamily="Splatfont2">Future and past month</Text></Radio>
                            </Flex>
                        </RadioGroup>
                    </Flex>
                </CalendarCard>

                <CalendarCard
                    title="Special Events"
                    bannerImage={eventsImage}
                    baseUrl={`${CALENDAR_SERVER}/special?lang=${localStorage.getItem("splatoonCalendarLocale")}&scope=${specialScope}`}
                >
                    <Flex direction="column">
                        <Text fontFamily="Splatfont2">
                            This calendar includes all the special events, including Splatfests, Big Runs, Eggstra Work, and Challenges.
                        </Text>
                        <Text mt={2} fontFamily="Splatfont2" alignItems="left">Calendar scope:</Text>
                        <RadioGroup onChange={setSpecialScope} value={specialScope}>
                            <Flex direction="row" gap={6}>
                                <Radio value="future"><Text fontFamily="Splatfont2">Future only</Text></Radio>
                                <Radio value="month"><Text fontFamily="Splatfont2">Future and past month</Text></Radio>
                            </Flex>
                        </RadioGroup>
                    </Flex>
                </CalendarCard>

            </Grid>
        </Box>
        <AppMenuButtons pageLabel="Splatoon" appVersion="0.3.2" modalHeader="About The Calendars" customComponent={CalendarLocaleSelect}>
            <p>I made this calendar feed with <Link isExternal href="https://github.com/misenhower/splatoon3.ink/wiki/Data-Access">misenhower's data API</Link>. The calendars use the ICS format so they're compatible with most calendar apps.</p>
            <br />
            <p>To use this feed, select a language on the top, then check the stages/modes/rules. Check none if you want to include all of them. The WebCal link will open your default calendar app (recommended for all iOS users). Google Calendar link will add the calendar to your Google account. Alternatively, you can copy the calendar link and subscribe to it in any third-party calendar apps.</p>
            <br />
            <p>The calendars are updated hourly and may have a minor delay.</p>
            <br />
            <Image src={splashtag} />
        </AppMenuButtons>
    </Center>;
}

export default CalendarFeed;